import React, { useState } from "react";
import FloatingCard from "../../../ui/FloatingCard";
import ScoringModeToggle from "../../live/components/ScoringModeToggle";
import PermissionBadge from "../common/PermissionBadge";
import BasicScoreBox from "../common/BasicScoreBox";
import InlinePlayerGrid from "./InlinePlayerGrid";

const TwoParticipantScoreboard = ({
  match,
  currentScore,
  isReadOnlyMode,
  connected,
  isMatchActive,
  onScoreIncrease,
  onScoreDecrease,
  onResetScore,
  onToggleTimeout,
  onCompleteMatch,
  onStartMatch,
  matchDuration,
  isTimeoutActive,
  scoringRules,
  scoringMode,
  onScoringModeChange,
  advancedSupported = false,
  title = "🏆 Live Score",
  announcerMuted,
  onAnnouncerToggleMute,
  onAnnouncerRepeat,
  p1Players = [],
  p2Players = [],
  onPlayerSelect,
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const maxPoints = scoringRules?.max_points ?? null;

  // Is a participant already at or above the cap?
  const p1AtMax = maxPoints !== null && maxPoints !== 999 && (currentScore?.participant1 || 0) >= maxPoints;
  const p2AtMax = maxPoints !== null && maxPoints !== 999 && (currentScore?.participant2 || 0) >= maxPoints;
  return (
    <FloatingCard
      variant="gradient"
      size="large"
      className={`score-display-websocket delay-3${isMaximized ? ' score-display-websocket--maximized' : ''}`}
      glow={isMatchActive}
    >
      <div className="score-header-websocket">
        <div className="score-header-top">
          <h3>{title}</h3>
          <div className="score-header-top-right">
            {!isReadOnlyMode && onScoringModeChange && (
              <ScoringModeToggle
                mode={scoringMode || 'basic'}
                onChange={onScoringModeChange}
                sportSupported={advancedSupported}
                disabled={!isMatchActive}
              />
            )}
            <PermissionBadge isReadOnlyMode={isReadOnlyMode} />
            <button
              className={`live-ctrl-btn live-ctrl-maximize${isMaximized ? ' active' : ''}`}
              onClick={() => setIsMaximized((m) => !m)}
              title={isMaximized ? 'Restore scoring panel' : 'Expand to full screen'}
            >
              {isMaximized ? '⊡ Restore' : '⛶ Expand'}
            </button>
          </div>
        </div>

        {/* Score Announcer pill — shown to editors and viewers alike; viewers
            default to muted and can opt in via the mute toggle. */}
        {isMatchActive && onAnnouncerToggleMute && (
          <div className="score-announcer-bar">
            <button
              className={`announcer-btn${announcerMuted ? ' muted' : ''}`}
              onClick={onAnnouncerToggleMute}
              title={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
              aria-label={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
            >
              {announcerMuted ? '🔇' : '🔊'}
            </button>
            <span className="announcer-label">Score Announcer</span>
            <button
              className="announcer-btn"
              onClick={onAnnouncerRepeat}
              title="Repeat last score announcement"
              aria-label="Repeat last score announcement"
            >
              🔁
            </button>
          </div>
        )}

        {!isReadOnlyMode && (
          <div className="live-match-quick-controls">
            {match?.status === "scheduled" && onStartMatch && (
              <button
                className="live-ctrl-btn live-ctrl-start"
                onClick={onStartMatch}
                title="Start Match"
              >
                🎬 Start Match
              </button>
            )}
            {isMatchActive && (
              <>
                <button
                  className="live-ctrl-btn live-ctrl-reset"
                  onClick={onResetScore}
                  title="Reset Score to 0 – 0"
                >
                  🔄 Reset Score
                </button>
                <button
                  className={`live-ctrl-btn live-ctrl-timeout${isTimeoutActive ? ' active' : ''}`}
                  onClick={onToggleTimeout}
                  title={isTimeoutActive ? "Resume Play" : "Call Timeout"}
                >
                  {isTimeoutActive ? '▶️ Resume' : '⏸️ Timeout'}
                </button>
                <button
                  className="live-ctrl-btn live-ctrl-complete"
                  onClick={() => onCompleteMatch && onCompleteMatch()}
                  title="Complete Match"
                >
                  🏁 Complete
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="participants-websocket">
        {/* Derive drop-in team info once for both sides */}
        {(() => {
          const dit = (match.matchConfig || match.match_config)?.drop_in_teams;
          const p1Name = dit ? (dit.team_a?.name || 'Team A') : (match.participant_1_name || match.participant1Name || `Participant ${match.participant1Id}`);
          const p2Name = dit ? (dit.team_b?.name || 'Team B') : (match.participant_2_name || match.participant2Name || `Participant ${match.participant2Id}`);
          const p1Label = dit ? '⚽ Drop-in Team' : (match.participant1Type === 'team' || match.participant_1_type === 'team' ? '👥 Team' : null);
          const p2Label = dit ? '⚽ Drop-in Team' : (match.participant2Type === 'team' || match.participant_2_type === 'team' ? '👥 Team' : null);

          const isAdvancedInline = scoringMode === 'advanced' && advancedSupported && !!onPlayerSelect;

          // Called as a function so the player icons are not remounted on every render
          const renderScoreControls = (participant, players) => {
            if (isReadOnlyMode) return null;
            const atMax     = participant === 'participant1' ? p1AtMax : p2AtMax;
            const score     = participant === 'participant1' ? (currentScore?.participant1 || 0) : (currentScore?.participant2 || 0);
            const subBlocked = score <= 0;

            if (isAdvancedInline) {
              return (
                <div className="vb-adv-inline">
                  <InlinePlayerGrid participant={participant} players={players} isDisabled={atMax} onPlayerSelect={onPlayerSelect} />
                  <button className="vb-deduct-btn" onClick={() => onScoreDecrease(participant)} disabled={subBlocked}>−1</button>
                </div>
              );
            }

            // Basic mode is rendered via the clickable BasicScoreBox below
            return null;
          };

          return (
            <>
              <div className="vb-participant-col">
                <div className="vb-team-name">{p1Name}{p1Label && <span className="participant-type">{p1Label}</span>}</div>
                {!isReadOnlyMode && !isAdvancedInline ? (
                  <BasicScoreBox
                    participantClassName="participant-1"
                    score={currentScore?.participant1 || 0}
                    disabled={p1AtMax}
                    subDisabled={(currentScore?.participant1 || 0) <= 0}
                    onIncrease={() => onScoreIncrease('participant1')}
                    onDecrease={() => onScoreDecrease('participant1')}
                    addLabel={`Add 1 point for ${p1Name}`}
                    subLabel={`Subtract 1 point for ${p1Name}`}
                  />
                ) : (
                  <div className="participant-websocket participant-1">
                    <div className="participant-score-large">{currentScore?.participant1 || 0}</div>
                    {renderScoreControls("participant1", p1Players)}
                  </div>
                )}
              </div>

              <div className="vs-divider-websocket">
                <div className="vs-circle-websocket">VS</div>
                {matchDuration && <div className="vs-duration">⏱ {matchDuration}</div>}
              </div>

              <div className="vb-participant-col">
                <div className="vb-team-name">{p2Name}{p2Label && <span className="participant-type">{p2Label}</span>}</div>
                {!isReadOnlyMode && !isAdvancedInline ? (
                  <BasicScoreBox
                    participantClassName="participant-2"
                    score={currentScore?.participant2 || 0}
                    disabled={p2AtMax}
                    subDisabled={(currentScore?.participant2 || 0) <= 0}
                    onIncrease={() => onScoreIncrease('participant2')}
                    onDecrease={() => onScoreDecrease('participant2')}
                    addLabel={`Add 1 point for ${p2Name}`}
                    subLabel={`Subtract 1 point for ${p2Name}`}
                  />
                ) : (
                  <div className="participant-websocket participant-2">
                    <div className="participant-score-large">{currentScore?.participant2 || 0}</div>
                    {renderScoreControls("participant2", p2Players)}
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>
    </FloatingCard>
  );
};

export default TwoParticipantScoreboard;
